<template>
	<div class="max-w-4xl px-4 mx-auto lg:px-0">
		<div class="flex pt-8 sm:pt-16 pb-4">
			<NuxtLink
				:id="BACK_TO_BLOG_BUTTON_ID"
				:to="localePath('/blog')"
				class="text-primary hover:underline underline-offset-2 decoration-2 decoration-primary"
				aria-label="Back to blog"
				@click="trackEvent($event, BLOG_ARTICLE)"
				@mouseenter="trackEvent($event, BLOG_ARTICLE)"
			>
				&lt; {{ $t('blog-article.back') }}
			</NuxtLink>
		</div>
		<div class="w-full aspect-h-1 aspect-w-1 sm:aspect-h-9 sm:aspect-w-16">
			<NuxtImg
				v-if="hasImage"
				provider="storyblok"
				sizes="324px sm:500px lg:800px xl:1000px"
				:src="`${imageSrc}`"
				:alt="article.image?.alt"
				class="object-cover h-full sm:w-full rounded-lg"
			/>
			<div
				v-else
				class="w-full overflow-hidden border border-(--ui-border-muted) rounded-lg shadow hover:shadow-lg aspect-w-16 aspect-h-9"
			>
				<div class="flex items-center content-center w-full justify-items-center">
					<UProgress color="neutral" />
				</div>
			</div>
		</div>
		<div
			v-if="hasDate"
			class="pt-4 text-dimmed"
		>
			{{ getDate(dateTime, locale) }}
		</div>
		<div class="mx-auto mb-12">
			<h1 class="pt-12 pb-8 sm:py-12 mb-4 text-3xl font-bold lg:text-4xl text-center text-highlighted">
				{{ article?.title }}
			</h1>
			<div
				class="min-w-full prose lg:prose-lg prose-h2:text-default"
				v-html="resolvedRichText"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { BACK_TO_BLOG_BUTTON_ID } from '@/constants/ga4/tracking-ids'
import { BLOG_ARTICLE } from '@/constants/ga4/tracking-locations'
import {
	BASE_URL,
	COMPANY_NAME
} from '@/constants/index.js'
import { JSON_LD_LOGO } from '@/constants/json-ld'
import type { BlogProps } from '@/types/components/blog'
import { getDate } from '@/utils/getDate'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { trackEvent } = useGA4Tracking()
const { getStory } = useStoryblokWithErrorReporting()

const route = useRoute()
const { locale } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const storyblok = await getStory(`blog/${route?.params?.article}`)

const { story } = storyblok.value?.data || {}

const {
	title, description
} = story?.content?.metatags || {}

const article = computed(() => {
	return story?.content as BlogProps
})

const hasImage = computed<boolean>(() => {
	return !!article.value?.image
})

const dateTime = computed<string>(() => {
	return article.value?.dateTime || ''
})

const hasDate = computed<boolean>(() => {
	return !!dateTime.value
})

const imageSrc = computed(() => {
	return article.value?.image?.filename || ''
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => imageSrc.value,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => imageSrc.value,
	twitterCard: () => 'summary_large_image'
})

useHead({ // TODO: Add title?
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

definePageMeta({
	h1Elsewhere: true
})

const resolvedRichText = computed(() => renderRichText(article.value?.content))

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Blog',
					'item': `${BASE_URL}/blog`
				}, {
					'@type': 'ListItem',
					'position': 3,
					'name': title,
					'item': `${BASE_URL}${route?.fullPath}`
				}
			]
		},
		{
			'@type': 'BlogPosting',
			'mainEntityOfPage': {
				'@type': 'WebPage',
				'@id': `${BASE_URL}${route?.fullPath}`
			},
			'headline': title,
			'image': imageSrc.value,
			description,
			'author': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'url': BASE_URL
			},
			'publisher': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'logo': {
					'@type': 'ImageObject',
					'url': JSON_LD_LOGO
				}
			},
			'datePublished': dateTime.value?.split(' ')[0] // split "2023-09-10" from "2023-09-10 00:00:00"
		}
	]
})
</script>
